import React, { useState } from 'react';
import Button from '@mui/material/Button';
import ButtonGroup from '@mui/material/ButtonGroup';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Grow from '@mui/material/Grow';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import MenuItem from '@mui/material/MenuItem';
import MenuList from '@mui/material/MenuList';
import { ButtonProps, ButtonGroupProps } from '@mui/material';

interface SplitButtonOption extends ButtonProps {
  label: string;
  id: string;
}

interface SplitButtonProps {
  options: SplitButtonOption[];
  defaultOptionId: string;
  variant?: ButtonGroupProps['variant'];
  color?: ButtonGroupProps['color'];
  size?: ButtonGroupProps['size'];
}

/**
 * SplitButton component with a primary action and dropdown for additional options
 *
 * @param {Object} props - Component props
 * @param {Array} props.options - Array of option strings to display in the dropdown
 * @param {number} props.defaultOption - Index of the default selected option
 * @param {string} props.variant - Button variant ('contained', 'outlined', 'text')
 * @param {string} props.color - Button color ('primary', 'secondary', 'error', etc.)
 * @param {string} props.size - Button size ('small', 'medium', 'large')
 * @param {Function} props.onClick - Callback fired when the main button is clicked
 * @param {Function} props.onOptionClick - Callback fired when an option is selected
 * @param {Object} props.buttonProps - Additional props to pass to the Button component
 */
const SplitButton: React.FC<SplitButtonProps> = ({
  options,
  defaultOptionId,
  variant = 'contained',
  color = 'primary',
  size = 'medium',
}) => {
  const [open, setOpen] = useState(false);
  const [selectedId, setSelectedId] = useState(defaultOptionId);
  const anchorRef = React.useRef<HTMLDivElement>(null);

  const handleMenuItemClick = (event: React.MouseEvent<HTMLLIElement>, id: string) => {
    setSelectedId(id);
    setOpen(false);
  };

  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const handleClose = (event: Event) => {
    if (anchorRef.current && anchorRef.current.contains(event?.target as Node)) {
      return;
    }

    setOpen(false);
  };
  const buttonProps = options.find((option) => option.id === selectedId) || {};

  return (
    <>
      <ButtonGroup
        variant={variant}
        color={color}
        size={size}
        ref={anchorRef}
        aria-label="split button"
      >
        <Button {...buttonProps}>{buttonProps?.label}</Button>
        <Button
          size={size}
          color={color}
          variant={variant}
          aria-controls={open ? 'split-button-menu' : undefined}
          aria-expanded={open ? 'true' : undefined}
          aria-label="select option"
          aria-haspopup="menu"
          onClick={handleToggle}
        >
          <ArrowDropDownIcon />
        </Button>
      </ButtonGroup>
      <Popper
        sx={{ zIndex: 1 }}
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        transition
        disablePortal
      >
        {({ TransitionProps, placement }) => (
          <Grow
            {...TransitionProps}
            style={{
              transformOrigin: placement === 'bottom' ? 'center top' : 'center bottom',
            }}
          >
            <Paper>
              <ClickAwayListener onClickAway={handleClose}>
                <MenuList id="split-button-menu" autoFocusItem>
                  {options.map((option, index) => (
                    <MenuItem
                      key={option.id}
                      selected={option.id === selectedId}
                      onClick={(event) => handleMenuItemClick(event, option.id)}
                    >
                      {option.label}
                    </MenuItem>
                  ))}
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </>
  );
};

export default SplitButton;
